import { 
  Product, 
  CreateProductRequest, 
  UpdateProductRequest, 
  ProductFilters, 
  PaginatedResponse, 
  ApiResponse,
  ProductCategory 
} from '../types';
import { apiClient } from './apiClient';

class ProductService {
  private readonly baseUrl = '/products';

  // Get all products with filters and pagination
  async getProducts(
    filters: ProductFilters = {}, 
    page: number = 1, 
    perPage: number = 10,
    includeCustomFields: boolean = false
  ): Promise<PaginatedResponse<Product>> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.sortBy) params.append('sort_by', filters.sortBy);
    if (filters.sortOrder) params.append('sort_order', filters.sortOrder);
    if (includeCustomFields) params.append('include_custom_fields', 'true');
    
    params.append('page', page.toString());
    params.append('per_page', perPage.toString());

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    return await apiClient.get<PaginatedResponse<Product>>(url);
  }

  // Get single product by ID
  async getProduct(id: number): Promise<Product> {
    const response = await apiClient.get<ApiResponse<Product>>(`${this.baseUrl}/${id}`);
    console.log('🔍 Raw API Response:', JSON.stringify(response, null, 2));
    console.log('🔍 Response keys:', Object.keys(response));
    console.log('🔍 Response.success:', response.success);
    console.log('🔍 Response.data:', response.data);
    
    if (!response.success || !response.data) {
      console.error('❌ Invalid response structure');
      throw new Error(response.message || 'Failed to fetch product');
    }
    
    console.log('✅ Returning product data:', JSON.stringify(response.data, null, 2));
    return response.data;
  }

  // Create new product
  async createProduct(product: CreateProductRequest): Promise<Product> {
    const response = await apiClient.post<ApiResponse<Product>>(this.baseUrl, product);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to create product');
    }
    return response.data;
  }

  // Update existing product
  async updateProduct(product: UpdateProductRequest): Promise<Product> {
    const response = await apiClient.put<ApiResponse<Product>>(`${this.baseUrl}/${product.id}`, product);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update product');
    }
    return response.data;
  }

  // Delete product
  async deleteProduct(id: number): Promise<void> {
    console.log('Deleting product with ID:', id, 'URL:', `${this.baseUrl}/${id}`);
    const response = await apiClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete product');
    }
  }

  // Get product categories
  async getCategories(): Promise<ProductCategory[]> {
    const response = await apiClient.get<ApiResponse<ProductCategory[]>>('/product-categories');
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch categories');
    }
    return response.data;
  }

  // Search products by barcode
  async searchByBarcode(barcode: string): Promise<Product | null> {
    try {
      const response = await apiClient.get<ApiResponse<Product>>(`${this.baseUrl}/search/barcode/${barcode}`);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Barcode search failed:', error);
      return null;
    }
  }

  // Get low stock products
  async getLowStockProducts(): Promise<Product[]> {
    const response = await apiClient.get<ApiResponse<Product[]>>(`${this.baseUrl}/low-stock`);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch low stock products');
    }
    return response.data;
  }

  // Get expiring products
  async getExpiringProducts(days: number = 30): Promise<Product[]> {
    const response = await apiClient.get<ApiResponse<Product[]>>(`${this.baseUrl}/expiring?days=${days}`);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch expiring products');
    }
    return response.data;
  }

  // Update stock quantity
  async updateStock(id: number, quantity: number, type: 'add' | 'subtract' = 'add'): Promise<Product> {
    const response = await apiClient.put<ApiResponse<Product>>(`${this.baseUrl}/${id}/stock`, {
      quantity,
      type
    });
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update stock');
    }
    return response.data;
  }

  // Bulk operations
  async bulkDelete(ids: number[]): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(`${this.baseUrl}/bulk-delete`, { ids });
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete products');
    }
  }

  async bulkUpdateStatus(ids: number[], status: Product['status']): Promise<void> {
    const response = await apiClient.put<ApiResponse<void>>(`${this.baseUrl}/bulk/status`, { ids, status });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update product status');
    }
  }

  // Legacy methods for backward compatibility
  async getAll(search?: string, page?: number, limit?: number): Promise<ApiResponse<{
    products: Product[];
    total: number;
    page: number;
    totalPages: number;
  }>> {
    const filters: ProductFilters = { search: search || '' };
    const response = await this.getProducts(filters, page || 1, limit || 10);
    return {
      success: true,
      data: {
        products: response.data,
        total: response.total,
        page: response.current_page,
        totalPages: response.last_page
      },
      message: 'Products retrieved successfully'
    };
  }

  async getById(id: number): Promise<ApiResponse<Product>> {
    try {
      const product = await this.getProduct(id);
      return {
        success: true,
        data: product,
        message: 'Product retrieved successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to fetch product',
        data: undefined
      };
    }
  }

  async create(product: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<Product>> {
    try {
      const createdProduct = await this.createProduct(product as CreateProductRequest);
      return {
        success: true,
        data: createdProduct,
        message: 'Product created successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create product',
        data: undefined
      };
    }
  }

  async update(id: number, product: Partial<Product>): Promise<ApiResponse<Product>> {
    try {
      const updatedProduct = await this.updateProduct({ ...product, id } as UpdateProductRequest);
      return {
        success: true,
        data: updatedProduct,
        message: 'Product updated successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update product',
        data: undefined
      };
    }
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    try {
      await this.deleteProduct(id);
      return {
        success: true,
        message: 'Product deleted successfully',
        data: undefined
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to delete product',
        data: undefined
      };
    }
  }

  async getLowStock(_threshold: number = 10): Promise<ApiResponse<Product[]>> {
    try {
      const products = await this.getLowStockProducts();
      return {
        success: true,
        data: products,
        message: 'Low stock products retrieved successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to fetch low stock products',
        data: []
      };
    }
  }

  async getExpiringSoon(days: number = 30): Promise<ApiResponse<Product[]>> {
    try {
      const products = await this.getExpiringProducts(days);
      return {
        success: true,
        data: products,
        message: 'Expiring products retrieved successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to fetch expiring products',
        data: []
      };
    }
  }
}

export const productService = new ProductService();
